import { ModeType, TopLevelModes } from "@/lib/modes";
import { MousePointerClick, TextCursorInput } from "lucide-react";
import React, { useCallback, useEffect } from "react";

import BaseChatComponent from "@/components/modes/base-chat";
import ImprotestComponent from "@/components/modes/improtest";
import { LanguageProficiency } from "@/context/learn-language-context";
import { VoiceChatMessage } from "@/lib/messaging";
import { useTranslation } from "react-i18next";

interface ModeSidePanelProps {
  mode: ModeType;
  languageProficiency: LanguageProficiency;
  newMessage?: VoiceChatMessage;
  transcript?: string;
  interimTranscript?: string;
  showTranscript?: boolean;
  setAudioPlaying: (isPlaying: boolean) => void;
  sendWsMessage: (message: VoiceChatMessage) => void;
  restartMode: () => void;
}

export function ModeSidePanelComponent({
  mode,
  languageProficiency,
  newMessage,
  transcript,
  interimTranscript,
  setAudioPlaying,
  sendWsMessage,
  restartMode,
}: ModeSidePanelProps) {
  const { t } = useTranslation(["common"]);
  const [messages, setMessages] = React.useState<VoiceChatMessage[]>([]);
  const [internalMode, setInternalMode] = React.useState<ModeType>(mode);
  const [resetToggle, setResetToggle] = React.useState<boolean>(false);

  useEffect(() => {
    console.log("New message received in ModeSidePanel:", newMessage);
    // TODO: Remove or keep for improtest resetToggle?
    if (!newMessage) {
      setMessages([]);
      setAudioPlaying(false);
      setResetToggle((prev) => !prev);
      return;
    }
    setMessages((prevMessages): VoiceChatMessage[] => [
      ...prevMessages,
      newMessage,
    ]);
  }, [newMessage]);

  const appendMessage = useCallback(
    (message: VoiceChatMessage) => {
      setMessages((prevMessages): VoiceChatMessage[] => [
        ...prevMessages,
        message,
      ]);
    },
    [setMessages],
  );

  useEffect(() => {
    setMessages([]);
    setAudioPlaying(false);
    setInternalMode(mode);
  }, [mode, languageProficiency]);

  return (
    <div
      className="border rounded-md bg-background h-[calc(100dvh-360px)] mb-10 md:mb-0 md:h-[calc(100dvh-144px)] flex flex-col"
      data-tour="mode-side-panel"
    >
      <p className="px-4 py-3 text-xs text-muted-foreground leading-5">
        <TextCursorInput className="h-4 w-4 inline-block align-middle mr-1" />
        <span>{t("wordTranslationHintPart1")} </span>
        <MousePointerClick className="h-4 w-4 inline-block align-middle mr-1" />
        <span>{t("wordTranslationHintPart2")}</span>
      </p>
      <div className="px-4 flex-1 overflow-y-auto pt-3 pb-8 md:pb-0 min-h-0">
        {internalMode.topLevelMode === TopLevelModes.ImproTest ? (
          <ImprotestComponent
            messages={messages}
            resetSignal={resetToggle}
            setAudioPlaying={setAudioPlaying}
            appendMessage={appendMessage}
            sendWsMessage={sendWsMessage}
            restartMode={restartMode}
          />
        ) : (
          <BaseChatComponent
            messages={messages}
            setAudioPlaying={setAudioPlaying}
            restartMode={restartMode}
          />
        )}
      </div>
      <div className="ml-0 md:ml-10 flex flex-row">
        <div
          className=" md:inline  w-full p-3 md:pr-16 border border-primary rounded-md min-h-12 overflow-y-auto"
          data-tour="transcript-area"
        >
          {transcript}
          <span className="text-gray-400">{interimTranscript}</span>
        </div>
      </div>
    </div>
  );
}
